/**
 * m3 echo Frontend - Layer5 Benchmark Tab
 *
 * Tools tab for comparing Layer 5 analysis with and without Layer 1 (LLM scoring) context.
 * User selects a project, backend runs Layer 5 twice, results shown side-by-side.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Card,
  CardContent,
  Grid,
  LinearProgress,
  IconButton,
  Tooltip,
  Autocomplete,
  TextField,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RefreshIcon from '@mui/icons-material/Refresh';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
  layer5BenchmarkApi,
  Layer5BenchmarkRun,
  Layer5BenchmarkResult,
  Layer5BenchmarkStatus,
  Layer5BenchmarkProject,
} from '../../services/admin';
import Layer5BenchmarkDetailDialog from './Layer5BenchmarkDetailDialog';

// ===========================
// Helpers
// ===========================

const formatDateTime = (dateString: string | null): string => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getStatusColor = (status: string): 'default' | 'primary' | 'success' | 'error' | 'warning' => {
  switch (status) {
    case 'pending': return 'default';
    case 'running': return 'primary';
    case 'completed': return 'success';
    case 'failed': return 'error';
    case 'cancelled': return 'warning';
    default: return 'default';
  }
};

const getStatusLabel = (status: string): string => {
  switch (status) {
    case 'pending': return 'Ausstehend';
    case 'running': return 'Läuft';
    case 'completed': return 'Abgeschlossen';
    case 'failed': return 'Fehlgeschlagen';
    case 'cancelled': return 'Abgebrochen';
    default: return status;
  }
};

const getScoreColor = (score: number): string => {
  if (score >= 90) return '#4caf50';
  if (score >= 75) return '#8bc34a';
  if (score >= 60) return '#ff9800';
  if (score >= 40) return '#f44336';
  return '#d32f2f';
};

// ===========================
// Component
// ===========================

export const Layer5BenchmarkTab: React.FC = () => {
  // State
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Projects
  const [projects, setProjects] = useState<Layer5BenchmarkProject[]>([]);
  const [selectedProject, setSelectedProject] = useState<Layer5BenchmarkProject | null>(null);
  const [projectsLoading, setProjectsLoading] = useState(false);

  // Runs
  const [runs, setRuns] = useState<Layer5BenchmarkRun[]>([]);
  const [activeRunId, setActiveRunId] = useState<number | null>(null);
  const [runStatus, setRunStatus] = useState<Layer5BenchmarkStatus | null>(null);

  // Detail dialog
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedResult, setSelectedResult] = useState<Layer5BenchmarkResult | null>(null);

  // Actions
  const [starting, setStarting] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  // Polling ref
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // ===========================
  // Data Loading
  // ===========================

  const loadProjects = useCallback(async () => {
    setProjectsLoading(true);
    try {
      const response = await layer5BenchmarkApi.getProjects();
      setProjects(response.data);
    } catch (err: any) {
      console.error('Error loading projects:', err);
    } finally {
      setProjectsLoading(false);
    }
  }, []);

  const loadRuns = useCallback(async () => {
    setLoading(true);
    try {
      const response = await layer5BenchmarkApi.getRuns();
      setRuns(response.data);

      // Check for active run
      const active = response.data.find(
        (r) => r.status === 'running' || r.status === 'pending'
      );
      if (active) {
        setActiveRunId(active.id);
      } else {
        setActiveRunId(null);
        setRunStatus(null);
      }

      setError(null);
    } catch (err: any) {
      console.error('Error loading runs:', err);
      setError(err.response?.data?.detail || 'Fehler beim Laden der Benchmark-Läufe');
    } finally {
      setLoading(false);
    }
  }, []);

  const pollStatus = useCallback(async () => {
    if (!activeRunId) return;

    try {
      const response = await layer5BenchmarkApi.getStatus(activeRunId);
      setRunStatus(response.data);

      if (response.data.status !== 'running' && response.data.status !== 'pending') {
        // Clear active run immediately so progress card disappears
        setActiveRunId(null);
        setRunStatus(null);
        // Then refresh the runs list to show updated status + scores
        await loadRuns();
      }
    } catch (err) {
      console.error('Error polling status:', err);
      // If polling fails (e.g. after restart), clear active state and refresh
      setActiveRunId(null);
      setRunStatus(null);
      await loadRuns();
    }
  }, [activeRunId, loadRuns]);

  // ===========================
  // Effects
  // ===========================

  useEffect(() => {
    loadProjects();
    loadRuns();
  }, [loadProjects, loadRuns]);

  useEffect(() => {
    if (activeRunId) {
      pollingRef.current = setInterval(pollStatus, 2000);
    } else {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    }
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, [activeRunId, pollStatus]);

  // ===========================
  // Handlers
  // ===========================

  const handleStartBenchmark = async () => {
    if (!selectedProject) {
      setError('Bitte wählen Sie ein Projekt aus');
      return;
    }

    setStarting(true);
    setError(null);

    try {
      const response = await layer5BenchmarkApi.start(selectedProject.id);
      setSuccess('Benchmark gestartet');
      setTimeout(() => setSuccess(null), 3000);
      setActiveRunId(response.data.id);
      setSelectedProject(null);
      await loadRuns();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Fehler beim Starten des Benchmarks');
    } finally {
      setStarting(false);
    }
  };

  const handleCancelBenchmark = async () => {
    if (!activeRunId) return;
    setCancelling(true);
    try {
      await layer5BenchmarkApi.cancel(activeRunId);
      setSuccess('Benchmark wird abgebrochen');
      setTimeout(() => setSuccess(null), 3000);
      setActiveRunId(null);
      await loadRuns();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Fehler beim Abbrechen');
    } finally {
      setCancelling(false);
    }
  };

  const handleDeleteRun = async (runId: number) => {
    try {
      await layer5BenchmarkApi.delete(runId);
      setSuccess('Benchmark gelöscht');
      setTimeout(() => setSuccess(null), 3000);
      await loadRuns();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Fehler beim Löschen');
    }
  };

  const handleViewResults = async (run: Layer5BenchmarkRun) => {
    try {
      const response = await layer5BenchmarkApi.getResults(run.id);
      setSelectedResult(response.data);
      setDetailDialogOpen(true);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Fehler beim Laden der Ergebnisse');
    }
  };

  // ===========================
  // Render
  // ===========================

  const isRunning = !!activeRunId;
  const progressPercent = runStatus?.progress_percent || 0;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">Layer5 Benchmark</Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={loadRuns}
          disabled={loading}
        >
          Aktualisieren
        </Button>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Configuration Section */}
      {!isRunning && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="subtitle1" gutterBottom>
              Neuer Benchmark
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Vergleicht Layer 5 Analyse ohne LLM-Kontext mit Layer 5 + Layer 1 Scoring-Daten.
            </Typography>

            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={8}>
                <Autocomplete
                  options={projects}
                  loading={projectsLoading}
                  value={selectedProject}
                  onChange={(_, newValue) => setSelectedProject(newValue)}
                  getOptionLabel={(option) => `${option.name} (${option.search_term})`}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Projekt auswählen"
                      placeholder="Projekt suchen..."
                      size="small"
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      <Box>
                        <Typography variant="body2">{option.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.search_term} | {option.homepage_url}
                        </Typography>
                      </Box>
                    </li>
                  )}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Button
                  variant="contained"
                  startIcon={starting ? <CircularProgress size={20} /> : <PlayArrowIcon />}
                  onClick={handleStartBenchmark}
                  disabled={starting || !selectedProject}
                  fullWidth
                >
                  {starting ? 'Wird gestartet...' : 'Benchmark starten'}
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Progress Section */}
      {isRunning && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle1">
                {runStatus?.progress_message || 'Benchmark läuft...'}
              </Typography>
              <Button
                variant="outlined"
                color="warning"
                startIcon={cancelling ? <CircularProgress size={20} /> : <CancelIcon />}
                onClick={handleCancelBenchmark}
                disabled={cancelling}
              >
                Abbrechen
              </Button>
            </Box>

            <LinearProgress
              variant="determinate"
              value={progressPercent}
              sx={{
                mb: 1,
                height: 10,
                borderRadius: 1,
                '& .MuiLinearProgress-bar': {
                  borderRadius: 1,
                  background: progressPercent >= 100
                    ? 'linear-gradient(90deg, #4caf50, #81c784)'
                    : 'linear-gradient(90deg, #678BA3, #8AA4B8)',
                },
              }}
            />

            <Typography variant="body2" color="text.secondary">
              {runStatus?.progress_message || 'Wird vorbereitet...'}
              {' '}({Math.round(progressPercent)}%)
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Previous Runs */}
      <Card>
        <CardContent>
          <Typography variant="subtitle1" gutterBottom>
            Vorherige Läufe
          </Typography>

          {loading && <CircularProgress size={24} />}

          {!loading && runs.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              Noch keine Layer5 Benchmarks durchgeführt.
            </Typography>
          )}

          {!loading && runs.length > 0 && (
            <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Projekt</TableCell>
                    <TableCell>Erstellt</TableCell>
                    <TableCell align="center">Ohne LLM</TableCell>
                    <TableCell align="center">Mit LLM</TableCell>
                    <TableCell align="center">Delta</TableCell>
                    <TableCell align="center">Status</TableCell>
                    <TableCell align="center">Aktionen</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {runs.map((run) => {
                    const delta = run.overall_score_generic != null && run.overall_score_with_layer1 != null
                      ? Math.round(run.overall_score_with_layer1 - run.overall_score_generic)
                      : null;
                    return (
                      <TableRow key={run.id} hover>
                        <TableCell>{run.project_name}</TableCell>
                        <TableCell>{formatDateTime(run.created_at)}</TableCell>
                        <TableCell align="center">
                          {run.overall_score_generic != null ? (
                            <Chip
                              label={Math.round(run.overall_score_generic)}
                              size="small"
                              sx={{
                                bgcolor: getScoreColor(run.overall_score_generic),
                                color: 'white',
                                fontWeight: 'bold',
                              }}
                            />
                          ) : '-'}
                        </TableCell>
                        <TableCell align="center">
                          {run.overall_score_with_layer1 != null ? (
                            <Chip
                              label={Math.round(run.overall_score_with_layer1)}
                              size="small"
                              sx={{
                                bgcolor: getScoreColor(run.overall_score_with_layer1),
                                color: 'white',
                                fontWeight: 'bold',
                              }}
                            />
                          ) : '-'}
                        </TableCell>
                        <TableCell align="center">
                          {delta !== null ? (
                            <Chip
                              label={`${delta > 0 ? '+' : ''}${delta}`}
                              size="small"
                              color={delta > 0 ? 'success' : delta < 0 ? 'error' : 'default'}
                              variant="outlined"
                            />
                          ) : '-'}
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={getStatusLabel(run.status)}
                            color={getStatusColor(run.status)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Ergebnisse anzeigen">
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => handleViewResults(run)}
                                disabled={run.status !== 'completed'}
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Löschen">
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteRun(run.id)}
                                disabled={run.status === 'running' || run.status === 'pending'}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Layer5BenchmarkDetailDialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        result={selectedResult}
      />
    </Box>
  );
};

export default Layer5BenchmarkTab;
